import {
  Carousel,
  Flex,
  HEADER_HEIGHT,
  HeaderIcon,
  IconClose,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useCallback, useRef, useState } from 'react';

import { Modal } from 'react-native';

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const DOUBLE_TAP_SCALE = 2;
const DOUBLE_TAP_DELAY = 300;

const ZoomableImage = ({
  uri,
  width,
  height,
}: {
  uri: string;
  width: number;
  height: number;
}) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const lastTapRef = useRef(0);
  const pinchStartDistRef = useRef(0);
  const pinchStartScaleRef = useRef(1);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
        e.preventDefault();
        if (scale > MIN_SCALE) {
          setScale(MIN_SCALE);
          setTranslate({ x: 0, y: 0 });
        } else {
          setScale(DOUBLE_TAP_SCALE);
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    },
    [scale],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDistRef.current = Math.sqrt(dx * dx + dy * dy);
        pinchStartScaleRef.current = scale;
      }
    },
    [scale],
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(
          MAX_SCALE,
          pinchStartScaleRef.current * (dist / pinchStartDistRef.current),
        ),
      );
      setScale(newScale);
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length < 2 && scale <= MIN_SCALE) {
        setScale(MIN_SCALE);
        setTranslate({ x: 0, y: 0 });
      }
    },
    [scale],
  );

  return (
    <div
      style={{
        width,
        height,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: 'transform 0.2s ease',
          willChange: 'transform',
        }}
      >
        <RemoteImage
          uri={uri}
          width={width}
          height={height}
          resizeMode={'contain'}
        />
      </div>
    </div>
  );
};

export const DetailedImageViewer = (props: {
  visible: boolean;
  currentIndex: number;
  onCloseViewer: () => void;
  images: string[];
}) => {
  const { width, height } = useScreenSize();
  const [currentIndex, setCurrentIndex] = useState(props.currentIndex);

  const carouselHeight = height - HEADER_HEIGHT;

  return (
    <Modal visible={props.visible} transparent animationType={'none'}>
      <Flex flex={1} backgroundColor={'black'} alignItems={'center'}>
        <Flex
          position={'absolute'}
          top={0}
          left={0}
          right={0}
          zIndex={1}
          alignItems={'center'}
        >
          <Flex
            position={'relative'}
            height={HEADER_HEIGHT}
            alignItems={'flex-end'}
            width={width}
          >
            <Flex.Center
              height={HEADER_HEIGHT}
              position={'absolute'}
              left={0}
              right={0}
            >
              <Typography type={'subtitle2'} color={'content_always_white'}>
                {currentIndex + 1} / {props.images.length}
              </Typography>
            </Flex.Center>
            <HeaderIcon
              customColor={'content_always_white'}
              onPress={props.onCloseViewer}
            >
              <IconClose />
            </HeaderIcon>
          </Flex>
        </Flex>
        <Flex flex={1} width={width} margin={{ top: HEADER_HEIGHT }}>
          <Carousel
            data={props.images}
            width={width}
            height={carouselHeight}
            defaultIndex={props.currentIndex}
            loop={false}
            onSnapToItem={setCurrentIndex}
            renderItem={({ item }) => (
              <ZoomableImage uri={item} width={width} height={carouselHeight} />
            )}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
